import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Users } from 'lucide-react';

const projects = [
  { key: 'crypto', image: '/portfolio-1.jpg', time: '3', users: '3K+' },
  { key: 'shop', image: '/portfolio-2.jpg', time: '5', users: '5K+' },
  { key: 'edu', image: '/portfolio-3.jpg', time: '7', users: '2K+' },
  { key: 'med', image: '/portfolio-4.jpg', time: '4', users: '1.5K+' },
  { key: 'support', image: '/portfolio-5.jpg', time: '2', users: '8K+' },
  { key: 'event', image: '/portfolio-6.jpg', time: '3', users: '4K+' },
];

export default function PortfolioSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="portfolio" className="py-12 sm:py-16 lg:py-20 px-5 scroll-mt-16">
      <div className="max-w-lg sm:max-w-3xl lg:max-w-6xl mx-auto">
        <div className="reveal opacity-0 mb-6 lg:mb-10 lg:text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <span className="w-1 h-1 rounded-full bg-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-[0.12em]">{t('portfolio.label')}</p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">{t('portfolio.title')}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {projects.map((project) => (
            <div key={project.key} className="reveal opacity-0 glass-card rounded-2xl overflow-hidden">
              <div className="aspect-video overflow-hidden">
                <img
                  src={project.image}
                  alt={t(`portfolio.projects.${project.key}.name`)}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-4">
                <span className="inline-block text-[11px] text-[#00D4FF] glass-pill px-2.5 py-0.5 rounded-full uppercase tracking-wide mb-2">
                  {t(`portfolio.projects.${project.key}.category`)}
                </span>
                <h3 className="text-[#F0F2F5] text-[15px] font-semibold mb-1">{t(`portfolio.projects.${project.key}.name`)}</h3>
                <p className="text-[#B5BBC9] text-[12px] leading-relaxed mb-3 line-clamp-2">{t(`portfolio.projects.${project.key}.desc`)}</p>
                <div className="flex items-center gap-4 text-[#B5BBC9] text-[11px]">
                  <span className="flex items-center gap-1">
                    <Zap size={11} className="text-[#FFB020]" />
                    {project.time} {t('portfolio.stats.time')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={11} className="text-[#00D4FF]" />
                    {project.users}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
